import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { updateUnits } from "@Services/utils/helpers";
import _ from "lodash";

const hoopsModalSlice = createSlice({
  name: "HoopsModalStatus",
  initialState: [],
  reducers: {
    setShowOnlyModal: (state: any, action: PayloadAction<any>) => {
      const keysToShow = Array.isArray(action.payload) ? action.payload : [action.payload];

      Object.keys(state).forEach((key: any) => {
        if (!keysToShow.includes(key) && key !== "Viewer") {
          state[key].show = false;
        }
      });

      keysToShow.forEach((key: any) => {
        if (!Object.hasOwn(state, key)) {
          state[key] = {};
        }
        state[key].show = true;
      });
    },
    setModalStatus: (_state: any, action: PayloadAction<any>) => action.payload,
    setAnimationForIssueCreation: (state: any, action: PayloadAction<any>) => ({
      ...state,
      Issues: {
        ...state.Issues,
        flagForAnimation: action.payload,
      },
    }),
  },
});

const sheetActiveSlice = createSlice({
  name: "isSheetActive",
  initialState: false,
  reducers: {
    setIsSheetActive: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

const faceSelectionSlice = createSlice({
  name: "faceSelection",
  initialState: false,
  reducers: {
    setFaceSelection: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

type ModelPropertiesState = {
  data: Record<string, Record<number, any>>;
  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
};

const defaultModelProperties: ModelPropertiesState = {
  data: {}, // ✅ object, not []
  isLoading: true,
  isLoaded: false,
  isError: false,
};

const modelPropertiesSlice = createSlice({
  name: "modelProperties",
  initialState: defaultModelProperties,
  reducers: {
    setModelProperties: (state: any, action: PayloadAction<any>) => ({ ...state, ...action.payload }),
    modifyMetadataUnits: (state: any, action: PayloadAction<any>) => {
      const { fileVersionId, unitsNew, portalUnits } = action.payload;
      const filesProperties = state.data?.[fileVersionId];
      if (!filesProperties) return state;
      const updatedDataByNodeId = Object.entries(filesProperties).reduce<Record<string, any>>(
        (acc, [nodeId, node]: [string, any]) => {
          const updatedProperties = updateUnits(node.properties, unitsNew, portalUnits);
          const updatedTypeProperties = updateUnits(node.typeProperties, unitsNew, portalUnits);
          acc[nodeId] = {
            ...node,
            properties: updatedProperties,
            typeProperties: updatedTypeProperties,
          };
          return acc;
        },
        {},
      );
      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: updatedDataByNodeId,
        },
      };
    },
    updateCustomMetadataInModelProperties: (state: any, action: PayloadAction<any>) => {
      const { fileVersionId, nodeId, fieldsData, selectedFormTemplate } = action.payload;
      //Temp for demo
      let updatedData: any[] = [];
      if (Object.values(state.data[fileVersionId]).length <= 0) {
        const updatedCustomProperties: Record<string, unknown> = {};
        if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
          delete updatedCustomProperties[fieldsData._id];
        } else {
          updatedCustomProperties[fieldsData._id] = fieldsData.value;
        }
        nodeId.map((node: any) => {
          updatedData.push({
            fileVersionId,
            nodeId: node,
            selectedFormTemplate: { _id: selectedFormTemplate },
            customProperties: updatedCustomProperties,
          });
        });
      } else {
        updatedData = Object.values(state.data[fileVersionId]).map((item: any) => {
          if (item.fileVersionId === fileVersionId && nodeId.includes(item.nodeId)) {
            const updatedCustomProperties = { ...item.customProperties };
            if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
              delete updatedCustomProperties[fieldsData._id];
            } else {
              updatedCustomProperties[fieldsData._id] = fieldsData.value;
            }
            return {
              ...item,
              selectedFormTemplate: { _id: selectedFormTemplate },
              customProperties: updatedCustomProperties,
            };
          }
          return item;
        });
      }
      const updatedDataByNodeId = updatedData.reduce<Record<string, any>>((acc, item: any) => {
        acc[item.nodeId] = item;
        return acc;
      }, {});
      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: updatedDataByNodeId,
        },
      };
    },
    updateQuantityAndUnit: (state: any, action) => {
      const { fileVersionId, nodeId, unit, quantity } = action.payload;

      const node = _.get(state, ["data", fileVersionId, nodeId]);
      if (!node) return state;

      return {
        ...state,
        data: {
          ...state.data,
          [fileVersionId]: {
            ...state.data[fileVersionId],
            [nodeId]: {
              ...node,
              properties: {
                ...node.properties,
                Unit: unit ?? node.properties.Unit,
                Quantity: quantity ?? node.properties.Quantity,
              },
            },
          },
        },
      };
    },
    updateAssetDataInModelProperties: (state, reduxAction) => {
      const { property, data, action } = reduxAction.payload;
      for (const obj of data) {
        const nodeData = _.get(state, ["data", obj.fileVersionId, obj.nodeId]);
        if (nodeData) {
          if (!Array.isArray(nodeData.assetData)) {
            nodeData.assetData = [];
          }
          if (action === "add") {
            if (!nodeData.assetData.includes(property)) {
              nodeData.assetData.push(property);
            }
          } else if (action === "remove") {
            nodeData.assetData = nodeData.assetData.filter((item: any) => item != property);
          }
        }
      }
    },
  },
});

type ModifiedModelPropsState = {
  nodeId: number[] | number;
  fileVersionId: string;
  fieldsData: any;
} | null;

const modifiedModelPropSlice = createSlice({
  name: "modifiedModelProps",
  initialState: null as ModifiedModelPropsState,
  reducers: {
    setModifiedModelProps: (state, action) => {
      return action.payload;
    },
  },
});

const settingModalSlice = createSlice({
  name: "GetSettingModalData",
  initialState: {},
  reducers: {
    setSettingModalAPIData: (_state: any, action: PayloadAction<any>) => action.payload,
    updateSettingModalAPIData: (state: any, action: PayloadAction<any>) => ({ ...state, ...action.payload }),
  },
});

const nodeIdAndUniqueIdMapSlice = createSlice({
  name: "nodeIdAndUniqueIdMap",
  initialState: {},
  reducers: {
    setNodeIdAndUniqueIdMap: (state: any, action: PayloadAction<any>) => {
      const { fileVersionId, data } = action.payload;
      const cubeUniqueIdMap: Record<string, number[]> = {};
      for (const i in data) {
        const mapKey = data[i];
        if (!mapKey) continue;
        const existing = cubeUniqueIdMap[mapKey] ?? [];
        cubeUniqueIdMap[mapKey] = [...existing, Number(i)];
      }
      return {
        ...state,
        [fileVersionId]: {
          nodeIdMap: data,
          cubeUniqueIdMap,
        },
      };
    },
  },
});

const bottomToolbarSlice = createSlice({
  name: "isBottomToolbarRefreshRequired",
  initialState: false,
  reducers: {
    setBottomToolbarRefresh: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

const transparentElementsSlice = createSlice({
  name: "transparentElements",
  initialState: [],
  reducers: {
    updateTransparentElements: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

const DEFAULT_VIEWER_STATE = {
  isSidebarOpen: true,
  markupAnnotations: [],
  nonMarkupAnnotations: [],
  currentPageInfo: {
    pageNumber: 1,
    reason: null,
    zoomLevel: 1,
    xScrollPosition: 0,
    yScrollPosition: 0,
  },
  showAllIssueMarkups: false,
  reason: "reset_viewer_state",
  showObjectTreeShimmer: true,
};

const viewerStateSlice = createSlice({
  name: "viewerState",
  initialState: DEFAULT_VIEWER_STATE,
  reducers: {
    resetViewerState: (state: any) => ({ ...state, ...DEFAULT_VIEWER_STATE }),
    setViewerState: (state: any, action: PayloadAction<any>) => {
      return {
        ...state,
        ...action.payload,
        reason: action.payload.reason,
      };
    },
    updateViewerState: (state: any, action: PayloadAction<any>) => ({
      ...state,
      [action.payload.field]: action.payload.value,
      reason: action.payload.reason,
    }),
    setAnnotations: (state: any, action: PayloadAction<any>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      return {
        ...state,
        [annotationType]: action.payload.data,
        reason: action.payload.reason,
      };
    },
    deleteAnnotation: (state: any, action: PayloadAction<any>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const existing = state[annotationType] ?? [];
      return {
        ...state,
        [annotationType]: existing.filter((annotation: any) => annotation.id !== action.payload.data),
        reason: action.payload.reason,
      };
    },
    addAnnotation: (state: any, action: PayloadAction<any>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const isArray = Array.isArray(action.payload.data);
      const dataToAdd = isArray ? action.payload.data : [action.payload.data];
      const existing = state[annotationType] ?? [];
      return {
        ...state,
        [annotationType]: [...existing, ...dataToAdd],
        reason: action.payload.reason,
      };
    },
    updateAnnotationProperties: (state: any, action: PayloadAction<any>) => {
      const annotationType = action.payload.annotationType || "nonMarkupAnnotations";
      const { id, properties } = action.payload.data;
      const existing = state[annotationType] ?? [];
      const targetIndex = existing.findIndex((annotation: any) => annotation.id === id);
      if (targetIndex === -1) return state;
      const targetAnnotation = existing[targetIndex];
      const hasChanges = Object.entries(properties).some(([key, value]) => {
        const isEqual = _.isEqual(targetAnnotation[key], value);
        return !isEqual;
      });
      if (!hasChanges) {
        return {
          ...state,
          reason: "unchanged_annotations",
        };
      }
      const newAnnotations = [...existing];
      newAnnotations[targetIndex] = { ...newAnnotations[targetIndex], ...properties };
      return {
        ...state,
        [annotationType]: newAnnotations,
        reason: action.payload.reason,
      };
    },
    setCurrentPagePDF: (state: any, action: PayloadAction<any>) => ({
      ...state,
      currentPageInfo: action.payload,
    }),
    setShowObjectTreeShimmer: (state: any, action: PayloadAction<any>) => ({
      ...state,
      showObjectTreeShimmer: action.payload,
    }),
  },
});

const selectedNestedMeasurementsSlice = createSlice({
  name: "getSelectedNestedMeasurements",
  initialState: {},
  reducers: {
    setSelectedNestedMeasurements: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

const measurementAttributePopupSlice = createSlice({
  name: "measurementAttributePopup",
  initialState: { show: false, callback: null, structure: null },
  reducers: {
    setMeasurementAttributePopup: (_state: any, action: PayloadAction<any>) => ({ ...action.payload }),
  },
});

const viewSlice = createSlice({
  name: "viewReducer",
  initialState: null,
  reducers: {
    setDefaultView: (_state: any, action: PayloadAction<any>) => action.payload,
  },
});

export const { setModalStatus, setShowOnlyModal, setAnimationForIssueCreation } = hoopsModalSlice.actions;
export const { setIsSheetActive } = sheetActiveSlice.actions;
export const { setFaceSelection } = faceSelectionSlice.actions;
export const {
  setModelProperties,
  modifyMetadataUnits,
  updateCustomMetadataInModelProperties,
  updateAssetDataInModelProperties,
  updateQuantityAndUnit,
} = modelPropertiesSlice.actions;
export const { setSettingModalAPIData, updateSettingModalAPIData } = settingModalSlice.actions;
export const { setModifiedModelProps } = modifiedModelPropSlice.actions;
export const { setNodeIdAndUniqueIdMap } = nodeIdAndUniqueIdMapSlice.actions;
export const { setBottomToolbarRefresh } = bottomToolbarSlice.actions;
export const { updateTransparentElements } = transparentElementsSlice.actions;
export const {
  resetViewerState,
  setViewerState,
  updateViewerState,
  setAnnotations,
  deleteAnnotation,
  addAnnotation,
  updateAnnotationProperties,
  setCurrentPagePDF,
  setShowObjectTreeShimmer,
} = viewerStateSlice.actions;
export const { setSelectedNestedMeasurements } = selectedNestedMeasurementsSlice.actions;
export const { setMeasurementAttributePopup } = measurementAttributePopupSlice.actions;
export const { setDefaultView } = viewSlice.actions;

export const HoopsModalStatus = hoopsModalSlice.reducer;
export const isSheetActive = sheetActiveSlice.reducer;
export const faceSelection = faceSelectionSlice.reducer;
export const modelProperties = modelPropertiesSlice.reducer;
export const GetSettingModalData = settingModalSlice.reducer;
export const nodeIdAndUniqueIdMap = nodeIdAndUniqueIdMapSlice.reducer;
export const isBottomToolbarRefreshRequired = bottomToolbarSlice.reducer;
export const transparentElements = transparentElementsSlice.reducer;
export const viewerState = viewerStateSlice.reducer;
export const getSelectedNestedMeasurements = selectedNestedMeasurementsSlice.reducer;
export const measurementAttributePopup = measurementAttributePopupSlice.reducer;
export const viewReducer = viewSlice.reducer;
export const modifiedModelProps = modifiedModelPropSlice.reducer;
